import { CardExpiryStatus } from '@afterpay/types'
import { CardErrorReason, PaymentMethodType } from '../../model'
import { buildTranslationKey } from '../../utils/locales'
import { SwitchContext } from './types'

// TODO: Clean this up when inline arcade is 100% enabled. These are keys within summary:error.* that have inline context
export const isKeyWithInlineContext = (reason: string, context?: SwitchContext): boolean => {
  const keysWithInlineContext: Record<string, SwitchContext[]> = {
    cardExpiry: [SwitchContext.CannotSwitch],
    foreignCard: [],
    prepaidCard: [],
    invalidBrand: [SwitchContext.CannotSwitch],
    unacceptableCard: [SwitchContext.CannotSwitch, SwitchContext.Switched],
    unacceptableIssuerRestrictedCard: [SwitchContext.CannotSwitch, SwitchContext.Switched],
    fallbackGenericMessage: [SwitchContext.Switched],
    checkingNotAllowed: [SwitchContext.Switched],
    creditCardNotAllowed: [SwitchContext.CannotSwitch],
    cashAppCardIneligible: [SwitchContext.CannotSwitch, SwitchContext.Switched],
  }

  if (!Object.keys(keysWithInlineContext).includes(reason)) {
    return false
  }

  const validContexts = keysWithInlineContext[reason]

  const isValidNoContext = !context && validContexts.length === 0
  const isValidWithContext = !!context && validContexts.includes(context)

  return isValidNoContext || isValidWithContext
}

export const getPreferredPaymentMethodKey = (paymentMethodType: PaymentMethodType | undefined): string => {
  switch (paymentMethodType) {
    case PaymentMethodType.ACH_BANK_ACCOUNT:
      return 'bank'
    case PaymentMethodType.APPLE_PAY:
      return 'applePay'
    default:
      return 'card'
  }
}

// Reasons where the frontend has classified the failure as an expiry issue. Other reasons
// (Topaz unacceptableCard, policy creditCardNotAllowed, brand-level rejections, etc.) keep
// their own message paths even when the card also happens to be expired — those carry
// stronger or more specific guidance the user needs to act on.
const HAS_EXPIRED_OVERRIDE_REASONS: ReadonlyArray<CardErrorReason> = ['cardExpiry', 'cardExpiryLimitInMonths']

interface CardErrorMessageInput {
  reason: CardErrorReason
  isArcadeDesignLanguage: boolean
  switched: boolean | undefined
  cardExpiryStatus: CardExpiryStatus | undefined
  isAFFPBI: boolean
  preferredPaymentMethod: PaymentMethodType | undefined
}

interface CardErrorMessageSelection {
  messageKey: string
  context: string
}

/**
 * Picks the i18n message key + context for a card-related summary error.
 *
 * Three buckets:
 *
 * 1. Auto-switched fallback (`switched: true`) — the user has been moved to a fallback
 *    card. Render the friendly `*_switched` copy; never the has-expired override.
 *
 * 2. Has-expired override (`!switched`, arcade design language, `cardExpiryStatus === EXPIRED`,
 *    AND reason is one of `cardExpiry` / `cardExpiryLimitInMonths`) — swap to the dedicated
 *    "Your card has expired" copy. The reason-restriction is deliberate so policy/Topaz
 *    rejections stay on their own message paths:
 *      - `unacceptableCard` (Topaz) — issuer block / fraud rule / etc.
 *      - `creditCardNotAllowed`, `invalidBrand`, `unacceptableIssuerRestrictedCard` —
 *        stronger user-facing guidance (e.g. "credit cards aren't accepted, add a debit card").
 *
 * 3. Default — `summary:error.<reason>.message` with the inline context derived from
 *    `switched` + `isKeyWithInlineContext`. AFF/PBI overrides `creditCardNotAllowed` to the
 *    payment-method-specific key.
 */
export const selectCardErrorMessage = ({
  reason,
  isArcadeDesignLanguage,
  switched,
  cardExpiryStatus,
  isAFFPBI,
  preferredPaymentMethod,
}: CardErrorMessageInput): CardErrorMessageSelection => {
  const baseContext = switched ? SwitchContext.Switched : SwitchContext.CannotSwitch
  const inlineContext = isArcadeDesignLanguage && isKeyWithInlineContext(reason, baseContext) ? `${baseContext}Inline` : baseContext

  const useHasExpiredCopy =
    isArcadeDesignLanguage && !switched && cardExpiryStatus === CardExpiryStatus.EXPIRED && HAS_EXPIRED_OVERRIDE_REASONS.includes(reason)

  if (useHasExpiredCopy) {
    return { messageKey: 'summary:error.cardExpiry.message', context: 'hasExpiredInline' }
  }

  return {
    messageKey: buildTranslationKey({
      namespace: 'summary',
      segments: [
        'error',
        reason,
        {
          contexts: [[isAFFPBI && reason === 'creditCardNotAllowed', `message_aff_${getPreferredPaymentMethodKey(preferredPaymentMethod)}`]],
          default: 'message',
        },
      ],
    }),
    context: inlineContext,
  }
}
